import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { confirmSignIn, fetchAuthSession, signIn, signOut, type SignInInput } from '@aws-amplify/auth';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import sessionAPI from '../../api/sessionAPI';
import { GENERAL_ERROR_MESSAGE, ROLE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { Role, RejectedWithValueAction } from '../../types/common';
import type { CognitoInfo, ResetPasswordParams, TimssSessionState } from '../../types/session';

const initialState: TimssSessionState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  login: false,
  emailCodeRequired: false,
  newPasswordRequired: false,
  name: undefined,
  email: undefined,
  roles: undefined,
  sidebarOpen: false,
  displayRoleAttention: true,
};

const slice = createSlice({
  name: 'TimssSession',
  initialState,
  reducers: {
    startLoading(state: TimssSessionState) {
      state.isLoading = true;
    },

    stopLoading(state: TimssSessionState) {
      state.isLoading = false;
    },

    hasError(state: TimssSessionState, action: PayloadAction<string>) {
      // console.log(action.payload);
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = action.payload;
    },

    resetMessage(state: TimssSessionState) {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
    },

    backToLogin(state: TimssSessionState): void {
      // initialState
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
      state.login = false;
      state.emailCodeRequired = false;
      state.newPasswordRequired = false;
      state.name = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.sidebarOpen = false;
      state.displayRoleAttention = true;
    },

    requireNewPassword(state: TimssSessionState): void {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
      state.newPasswordRequired = true;
      state.emailCodeRequired = false;
    },

    completeNewPassword(state: TimssSessionState): void {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = 'Your password has been updated.';
      state.newPasswordRequired = false;
      state.emailCodeRequired = true;
    },

    requireEmailCode(state: TimssSessionState): void {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
      state.newPasswordRequired = false;
      state.emailCodeRequired = true;
    },

    cognitoLogout(state: TimssSessionState): void {
      // initialState
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
      state.login = false;
      state.emailCodeRequired = false;
      state.newPasswordRequired = false;
      state.name = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.sidebarOpen = false;
      state.displayRoleAttention = true;
    },

    checkSession(state: TimssSessionState, action: PayloadAction<CognitoInfo>): void {
      state.isLoading = false;
      const { name, email, roles } = action.payload;
      state.name = name;
      state.email = email;
      state.roles = roles;
      state.login = true;
    },

    sidebarToggle(state: TimssSessionState): void {
      state.sidebarOpen = !state.sidebarOpen;
    },

    hideRoleAttention(state: TimssSessionState): void {
      state.displayRoleAttention = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // Reset Password
      // ----------------------------------------
      .addCase(resetPassword.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        state.serverMessage = undefined;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<ResetPasswordParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      });
  },
});

export const { reducer, actions } = slice;

export const startLoading =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.startLoading());
  };
export const stopLoading =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.stopLoading());
  };

export const hasError =
  (message: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const sidebarToggle =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.sidebarToggle());
  };

export const hideRoleAttention =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hideRoleAttention());
  };

// ログイン画面に戻る
export const backToLogin =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.backToLogin());
  };

// 新パスワード設定フォームを表示する
export const requireNewPassword =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.requireNewPassword());
  };

// 認証コード入力フォームを表示する
export const requireEmailCode =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.requireEmailCode());
  };

// 新パスワードを設定する
export const completeNewPassword =
  (newPassword: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      console.log(nextStep);
      if (nextStep.signInStep === 'DONE') {
        dispatch(checkSession());
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        dispatch(requireEmailCode());
      } else {
        const message: string = 'Authentication failed. Please log in again.';
        dispatch(slice.actions.hasError(message));
      }
    } catch (err) {
      const message: string = getAsyncErrorMessage(err);
      dispatch(slice.actions.hasError(message));
    }
  };

// 認証コードを確認する
export const completeEmailCode =
  (emailCode: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/sign-in/#email-otp
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: emailCode,
      });
      if (nextStep.signInStep === 'DONE') {
        dispatch(checkSession());
      } else {
        const message: string = 'Authentication failed. Please log in again.';
        dispatch(slice.actions.hasError(message));
      }
    } catch (err) {
      const message: string = getAsyncErrorMessage(err);
      dispatch(slice.actions.hasError(message));
    }
  };

// ログイン
export const cognitoLogin =
  (params: SignInInput): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // amplifyのsignInフローに従う
    // https://docs.amplify.aws/gen1/react/build-a-backend/auth/enable-sign-up/#sign-in
    try {
      const { isSignedIn, nextStep } = await signIn(params);
      dispatch(slice.actions.stopLoading());
      // console.log(isSignedIn, nextStep);
      // https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/sign-in/
      switch (nextStep.signInStep) {
        // 初回ログイン時（仮パスワードでログインした場合）
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          dispatch(requireNewPassword());
          break;
        // MFA時
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
          dispatch(requireEmailCode());
          break;
        // 通常ログイン時
        case 'DONE':
          dispatch(checkSession());
          break;
        default:
          if (isSignedIn) {
            await signOut();
          }
          dispatch(slice.actions.hasError('Login failed.'));
          break;
      }
    } catch (err) {
      const message: string = getAsyncErrorMessage(err);
      dispatch(slice.actions.hasError(message));
    }
  };

// セッション情報をチェックしてクレーム情報を取得する。
// クレーム情報がそろっていなかったらログアウトする（initialStateにする）。
export const checkSession =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const session = await fetchAuthSession();
      // console.log(session);
      const payload = session?.tokens?.idToken?.payload;
      const { 'cognito:username': userName, email: userEmail, 'cognito:groups': userRoles } = payload ?? {};
      if (
        userName &&
        typeof userName === 'string' &&
        userEmail &&
        typeof userEmail === 'string' &&
        userRoles &&
        Array.isArray(userRoles) &&
        userRoles.length > 0 &&
        userRoles.every((role) => role === ROLE.USER || role === ROLE.POWERUSER || role === ROLE.ADMIN || role === ROLE.MAINTENANCE_ADMIN)
      ) {
        const cognitoInfo: CognitoInfo = {
          name: userName.toString(),
          email: userEmail.toString(),
          roles: userRoles as Role[],
        };
        dispatch(slice.actions.checkSession(cognitoInfo));
      } else {
        // 情報が揃っていないのであればログアウト
        await signOut();
        dispatch(slice.actions.cognitoLogout());
      }
    } catch (err) {
      const message: string = getAsyncErrorMessage(err);
      dispatch(slice.actions.hasError(message));
    }
  };

// client/src/components/layout/MainSidebar/MainSidebar.tsx
// のhandleLogout専用
export const cognitoLogout =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    // ログアウトを待ってからログインページに飛ばしたいので
    // エラーハンドリングはイベントハンドラに任せる
    await signOut();
    dispatch(slice.actions.cognitoLogout());
  };

// パスワードリセット
export const resetPassword = createAsyncThunk('ManageUser/resetPassword', async (params: ResetPasswordParams, thunkAPI) => {
  try {
    const response = await sessionAPI.resetPassword(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
